<template>
    <div class="ask-offer" data-testid="ask-box-offer">
        <span class="ask-offer__eyebrow">{{
            addsToDraft ? "Adds to your draft" : "Starts a thread"
        }}</span>

        <p class="ask-offer__title" data-testid="ask-box-offer-title">
            {{ offer.title }}
        </p>

        <p
            v-if="about"
            class="ask-offer__about"
            data-testid="ask-box-offer-about"
        >
            <i :class="about.icon" aria-hidden="true" />
            <span>{{ about.name }}</span>
        </p>

        <div class="ask-offer__first">
            <span class="ask-offer__label">First message</span>
            <!-- Rendered by the component the thread renders its entries
                 with, so the preview shows the message the way the thread will
                 show it — emphasis, lists and links included. What is posted
                 is still the raw `message`; only the rendering is shared. -->
            <ThreadMarkdown
                class="ask-offer__message"
                data-testid="ask-box-offer-message"
                :content="message"
            />
        </div>

        <Button
            class="ask-offer__button"
            data-testid="ask-box-offer-button"
            size="small"
            icon="pi pi-comments"
            :label="addsToDraft ? 'Add to draft' : 'Start thread'"
            :loading="isStarting"
            :disabled="isStarting"
            @click="start"
        />
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { entityTypeLabel } from "@/components/EntityInfo/entity-type-label";
import ThreadMarkdown from "@/components/Thread/ThreadMarkdown.vue";
import { entityIcon } from "@/components/Thread/reconcile/entity-icons";
import { draftIdFromQuery } from "@/composables/use-thread-branch-view";
import type {
    AskReference,
    AskVersion,
    ThreadOffer,
} from "@/lib/openapi/gen/types.gen";
import { ThreadContextKind } from "@/lib/openapi/gen/types.gen";
import { entityLocation } from "@/router/entity-location";
import { useThreadsStore } from "@/stores/threads-store";

/**
 * The thread the box offers when it has declined to change something — shown
 * before it exists, so the person knows what they are about to start.
 *
 * The box only reads; a change goes through a thread. The refusal already says
 * so in prose, and this is the same offer in a form that can be acted on —
 * which is the whole reason the answer carries a structured `offer_thread`
 * rather than leaving the UI to find the offer in English.
 *
 * **What is shown is what is posted, by construction.** The epic requires that
 * nothing from a private conversation reaches a thread without the exact words
 * being shown first. This used to be satisfied incidentally — the title was the
 * button's label, and the question happened to still be on screen above the
 * answer. Nobody pressing that button knew their sentence was about to be
 * posted somewhere other people read. Now the `message` prop is a single
 * string that this component renders verbatim *and* hands to `contribute`
 * verbatim, so the preview cannot say one thing while the thread receives
 * another. Whether the message is the question as typed or a brief the box
 * wrote from the conversation, nothing here changes; only what the caller
 * passes does.
 *
 * **Shown the way the thread will show it.** The thread renders every entry
 * as markdown, so a message with emphasis or a list would look one way here
 * and another way there if the preview showed raw text — and now that the
 * message can be something the box wrote, it may well contain both. The
 * preview renders through the same component the thread uses, so the two
 * cannot drift; the string handed to `contribute` is the raw source either
 * way. Same words in, same rendering out, at both ends.
 *
 * **Read-only, deliberately.** An editable box here would be a second place to
 * write a thread post, competing with the thread's own input — which the epic
 * rules out. If the message is not what someone wants, they close the box and
 * ask differently.
 *
 * **An answer about a draft adds to that draft.** A draft *is* a thread, so
 * starting a new one from inside a draft used to leave the reader with two
 * drafts side by side, the second knowing nothing of the first's changes. The
 * offer is worked out from the same version the answer was: when the answer's
 * own note says it described a draft, the message is posted into that draft's
 * thread instead. Keyed on the note rather than on where the reader is now,
 * because an answer about draft A must not land its message in draft B just
 * because they switched tabs meanwhile. A draft accepted or thrown away since
 * describes a version nobody can add to, so the offer then does what it
 * always did.
 *
 * Its own component rather than markup in `AskBox.vue` for the same reason as
 * `AskReferences.vue`: it needs a router, and that file's spec mounts without
 * one.
 */
const props = defineProps<{
    offer: ThreadOffer;
    /**
     * Exactly what becomes the thread's first entry. Rendered and posted as the
     * same string — that identity is the consent.
     */
    message: string;
    /**
     * The answer's references, already resolved against the stek. The offer
     * carries only an entity's type and id; this is where its name comes from.
     */
    references?: AskReference[];
    /**
     * Which version the answer described, as the server recorded it. A draft
     * here is the draft this offer adds to.
     */
    version?: AskVersion | null;
}>();

const emit = defineEmits<{ handedOff: [] }>();

const threads = useThreadsStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

const isStarting = ref(false);

/** The draft the answer described, when it described one. */
const describedDraft = computed(() =>
    props.version?.kind === "draft" && props.version.draft_id
        ? props.version.draft_id
        : null
);
const addsToDraft = computed(() => describedDraft.value !== null);

/**
 * What the thread will be attached to, said the way the reader knows it.
 *
 * The offer itself names nothing — `ThreadOffer` is a type and an id. The
 * answer's references are the same ids with names, and they have already been
 * through the stek gate, so a match there is the right name. With no match
 * the kind of thing is still worth saying ("Process"); with not even that,
 * the line is left out rather than shown empty.
 */
const about = computed(() => {
    const entityType = props.offer.entity_type;
    const entityId = props.offer.entity_id;
    if (!entityType || !entityId) {
        return null;
    }
    const named = props.references?.find(
        (reference) =>
            reference.entity_type === entityType &&
            reference.entity_id === entityId
    );
    const name = named?.entity_name || entityTypeLabel(entityType);
    if (!name) {
        return null;
    }
    return { icon: entityIcon(entityType), name };
});

/**
 * Post the message into the draft the answer described, if it is still open.
 *
 * Three outcomes, and the difference between the last two is the point.
 * `added`: the draft is open and the message was posted (or the post failed
 * and the reader was told — they are taken to the draft either way, as the
 * create path does after a failed post). `gone`: accepted, rejected or
 * deleted since, so there is nothing to add to and the caller falls back to
 * a new thread. `unknown`: the check itself failed. That one must *not*
 * fall back — a blip on this single read would create the second draft
 * beside the first that the offer exists to avoid, and silently — so the
 * caller says so and leaves the button pressable.
 *
 * `use-draft-editing` reads an unreadable record as "keep the draft"; that
 * path is about to read, this one is about to write, so it asks the store
 * for the distinction rather than guessing either way.
 */
const addToDraft = async (
    draftId: string
): Promise<"added" | "gone" | "unknown"> => {
    const state = await threads.readDraftState(draftId);
    if (state === "unreachable") {
        return "unknown";
    }
    if (state !== "open") {
        return "gone";
    }
    try {
        await threads.contribute(draftId, props.message);
    } catch (e) {
        console.error("Could not add the message to the draft", e);
        toast.add({
            severity: "warn",
            summary: "Your message did not post",
            detail: "Send it again from the draft's thread to get things moving.",
            life: 6000,
        });
    }
    return "added";
};

/**
 * Create the thread, post the message into it, and go there.
 *
 * Modelled on `threads-store.startProcessBpmnThread`, which is the same shape:
 * the entity goes in twice — once as the thread's subject, once as an ENTITY
 * context item for the agent — and the opening message is posted with
 * `contribute` rather than passed to `createThread`. That is not a detail: a
 * kind-less thread does not dispatch its agent on creation, so without the
 * contribution the thread would sit there with nobody working on it.
 *
 * `kind` is left unset on purpose. The three onboarding/research kinds demand
 * stek-manager on the endpoint, while a plain thread needs only the `stek.user`
 * the ask box already required to answer at all.
 */
const start = async () => {
    if (isStarting.value) {
        return;
    }
    isStarting.value = true;

    const entityType = props.offer.entity_type ?? undefined;
    const entityId = props.offer.entity_id ?? undefined;
    const hasEntity = Boolean(entityType && entityId);

    /**
     * Where the reader should end up, once the thread exists.
     *
     * One push lands in the draft with the entity page under it: the
     * application layout watches `?draft=` and switches reads to the
     * thread's git branch. It deliberately does *not* open the drawer for a
     * new thread — that is left to the reader — so the create path only
     * navigates. Adding to an existing draft opens the drawer as well, since
     * the thing just posted is in there and nothing else would show it; and
     * skips the push when the reader is already in that draft, where a
     * same-route push would change nothing.
     */
    const land = (threadId: string, { open = false } = {}) => {
        const alreadyThere = draftIdFromQuery(route.query) === threadId;
        const target = hasEntity
            ? entityLocation(
                  {
                      entity_type: entityType as string,
                      entity_id: entityId as string,
                  },
                  {
                      orgName: route.params.orgName,
                      stekName: route.params.stekName,
                  }
              )
            : undefined;

        if (target && !alreadyThere) {
            const pushed = router.push({
                ...(target as object),
                query: { draft: threadId },
            });
            if (open) threads.openDrawer(threadId);
            return pushed;
        }
        // A title-only offer, an entity with no page of its own, or a draft
        // the reader is already standing in. There is nowhere else to go, so
        // open the thread where the reader already is.
        threads.openDrawer(threadId);
        return Promise.resolve();
    };

    if (describedDraft.value !== null) {
        const outcome = await addToDraft(describedDraft.value);
        if (outcome === "unknown") {
            isStarting.value = false;
            toast.add({
                severity: "warn",
                summary: "Could not reach your draft",
                detail: "Nothing was posted. Try again in a moment.",
                life: 5000,
            });
            return;
        }
        if (outcome === "added") {
            isStarting.value = false;
            await land(describedDraft.value, { open: true });
            emit("handedOff");
            return;
        }
        // The draft is gone or closed: nothing to add to, so start a thread
        // as the offer always did.
    }

    let thread;
    try {
        thread = await threads.createThread(props.offer.title, {
            subjectEntityType: entityType,
            subjectEntityId: entityId,
            contextItems: hasEntity
                ? [
                      {
                          kind: ThreadContextKind.ENTITY,
                          data: {
                              entity_type: entityType as string,
                              entity_id: entityId as string,
                          },
                      },
                  ]
                : undefined,
        });
    } catch (e) {
        // The shared `api` singleton is `throwOnError: true`, so a non-2xx
        // rejects here rather than coming back as a value — which means
        // `createThread`'s own error toast never runs. Without this the button
        // would spin, stop, and say nothing, and the reader's only available
        // reading is that it is broken.
        console.error("Could not start a thread from the ask box", e);
        thread = undefined;
    } finally {
        isStarting.value = false;
    }

    if (!thread?.identity) {
        toast.add({
            severity: "error",
            summary: "Could not start a thread",
            detail: "Nothing was created. Try again in a moment.",
            life: 5000,
        });
        return;
    }

    // The thread exists from here on, so every path below still lands the
    // reader on it. An invisible thread is worse than a flawed one.
    try {
        await threads.contribute(thread.identity, props.message);
    } catch (e) {
        // The thread was created but the opening message did not land. That
        // leaves a thread with no entries at all and so no agent working on it
        // — the exact state the contribution exists to prevent. Take the reader
        // there anyway and say what is missing: they can post it themselves,
        // which is what wakes the agent.
        console.error("Could not post the opening message", e);
        toast.add({
            severity: "warn",
            summary: "Thread created, but your message did not post",
            detail: "Send it again from the thread to get things moving.",
            life: 6000,
        });
    }

    await land(thread.identity);
    emit("handedOff");
};
</script>

<style scoped>
/* A quiet block, not a card. The same hairline the reference chips use, and
   deliberately no mode colour: the box answers across Design, Build and Run
   at once, and a stripe here would claim a scope the thread does not yet have. */
.ask-offer {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 0.875rem;
    border: 1px solid var(--p-surface-300);
    border-radius: 6px;
}

.ask-offer__eyebrow,
.ask-offer__label {
    color: var(--p-text-muted-color);
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.ask-offer__title {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.35;
}

.ask-offer__about {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    margin: 0;
    color: var(--p-text-muted-color);
    font-size: 0.8rem;
}

.ask-offer__about i {
    font-size: 0.75rem;
}

.ask-offer__first {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

/* Shown whole, and shown as it will be posted; long words allowed to wrap
   rather than overflow. Truncating this would put the consent back to
   "technically visible", which is where it started. Line breaks are the
   renderer's business (`breaks: true`), so no `pre-wrap` here — it would
   turn the whitespace between rendered blocks into visible gaps. */
.ask-offer__message {
    margin: 0;
    padding: 0.5rem 0.625rem;
    background: var(--p-surface-100);
    border-radius: 4px;
    font-size: 0.85rem;
    line-height: 1.45;
    overflow-wrap: anywhere;
}

/* The same block spacing the answer above uses, so a brief with a list in it
   reads as one message rather than a stack of paragraphs. */
.ask-offer__message :deep(p) {
    margin: 0.35em 0;
}

.ask-offer__message :deep(p:first-child) {
    margin-top: 0;
}

.ask-offer__message :deep(p:last-child) {
    margin-bottom: 0;
}

.ask-offer__message :deep(ul),
.ask-offer__message :deep(ol) {
    margin: 0.35em 0;
    padding-left: 1.4em;
}

/* Left-aligned and small: this is an offer, not a call to action. The answer
   above it is still the content, and the refusal has already been read by the
   time anyone looks down here. */
.ask-offer__button {
    align-self: flex-start;
    font-size: 0.8rem;
}
</style>
